import React, { useMemo } from 'react';
import {
  FIDDLE_TUNING,
  FIDDLE_STRING_NAMES,
  FIDDLE_TAPE_SEMITONES,
  FIDDLE_DEGREE_LABELS,
  TOTAL_SEMITONES_FIDDLE,
  boardFraction,
  resolveFiddleFormula,
  buildFiddleNodes,
  buildFiddleDoubleStopNodes,
  buildFiddleArpeggioNodes,
  findHalfStepPairs,
  FIDDLE_POSITIONS,
} from '../../lib/fiddle-theory';
import { useIsDesktop } from '../../hooks/useIsDesktop';

// ── Layout constants ─────────────────────────────────────────────────────────
//
// The board is drawn VERTICALLY — nut at the top, strings running down, G D A E
// left to right. That is the violin fingering-chart convention and it is how a
// player sees their own neck; it also matches the upright bass fingerboard.
//
// The y axis is linear in DISTANCE ALONG THE STRING, which is what makes the
// non-linear note spacing visible: boardFraction() supplies the compression.

const VIEW_W       = 346;
const VIEW_H       = 732;
const NUT_Y        = 78;    // y of the nut
const BOARD_H      = 620;   // nut → octave, in view units
const BOARD_BOTTOM = 720;   // board is drawn slightly past the octave
const CX           = 165;   // board centre line

// The fingerboard tapers — narrow at the nut, wider at the body. The string
// spacing widens with it, so a note's x depends on how far down the board it is.
const SPREAD_NUT   = 38;    // gap between adjacent strings at the nut
const SPREAD_TAPER = 11;    // extra gap by the octave
const EDGE_NUT     = 84;    // board half-width at the nut
const EDGE_TAPER   = 34;    // extra half-width by the octave

// Rendered scale (screen px per view unit). Phone 0.74 puts the strings ~28px apart at the
// nut (the same density as the guitar on board 18); desktop 0.95 makes the board ~330px wide.
// Anything that must stay a fixed screen size (text, rings) is divided by it via px().
const SCALE_PHONE   = 0.74;
const SCALE_DESKTOP = 0.95;

// Dot outer radius = 74% of the 38-unit string gap at the nut (CLAUDE.md: 72-78%).
const NOTE_RADIUS  = 14;
const OPEN_Y       = 48;    // open-string circles sit above the nut
const NAME_Y       = 18;    // string name labels above those
const STRING_WIDTH = [2.6, 2.1, 1.7, 1.3];  // G thickest → E thinnest

// The rotated position label sits in the left margin, outside the board.
const POSITION_LABEL_X = 20;

// ── Geometry helpers ─────────────────────────────────────────────────────────

/** y for a note n semitones above the open string. */
const noteY = (n) => NUT_Y + boardFraction(n) * BOARD_H;
/** Normalised board position (0 = nut, 1 = octave) for a y coordinate. */
const fracAtY = (y) => (y - NUT_Y) / BOARD_H;
/** x of a string at a given normalised board position, accounting for the taper. */
const stringX = (s, frac) => CX + (s - 1.5) * (SPREAD_NUT + SPREAD_TAPER * frac);
/** Board half-width at a given normalised board position. */
const edgeAt = (frac) => EDGE_NUT + EDGE_TAPER * frac;

/** x/y of a note, treating semitone 0 as the open-string circle above the nut. */
function notePoint(string, semitone) {
  const frac = semitone === 0 ? 0 : boardFraction(semitone);
  return {
    x: stringX(string, frac),
    y: semitone === 0 ? OPEN_Y : NUT_Y + frac * BOARD_H,
  };
}

const round = (v) => Math.round(v * 100) / 100;

const BOARD_BOTTOM_FRAC = fracAtY(BOARD_BOTTOM);
const BOARD_POINTS = [
  `${CX - EDGE_NUT},${NUT_Y}`,
  `${CX + EDGE_NUT},${NUT_Y}`,
  `${round(CX + edgeAt(BOARD_BOTTOM_FRAC))},${BOARD_BOTTOM}`,
  `${round(CX - edgeAt(BOARD_BOTTOM_FRAC))},${BOARD_BOTTOM}`,
].join(' ');

// ── Component ────────────────────────────────────────────────────────────────

export default function FiddleFingerboard({
  fiddleKey,
  fiddleTonality,
  fiddleStructure,
  fiddleMode,
  fiddleShape,
  fiddlePosition,
  fiddleStringFocus,
  fiddleFilter,
  fiddleLabel,
  fiddleGuides,
  fiddleArpeggioType,
}) {
  const isDesktop = useIsDesktop();
  const k  = isDesktop ? SCALE_DESKTOP : SCALE_PHONE;
  /** A screen-pixel size expressed in view units. */
  const px = (v) => v / k;
  const RING        = px(1.25);
  const NOTE_FONT   = isDesktop ? 11 : 9;
  const NOTE_FONT_S = isDesktop ? 9.5 : 8;
  const boardGradientId = 'fiddle-board-fill';

  const showTapes     = fiddleGuides.includes('Tapes');
  const showHalfSteps = fiddleGuides.includes('Half Steps');
  const isDoubleStops = fiddleStructure === 'Double Stops';
  const isArpeggios   = fiddleStructure === 'Arpeggios';

  const formula = useMemo(
    () => resolveFiddleFormula(fiddleStructure, fiddleMode, fiddleTonality),
    [fiddleStructure, fiddleMode, fiddleTonality]
  );

  const { notes, pairs } = useMemo(() => {
    let built;
    if (isArpeggios) {
      built = {
        nodes: buildFiddleArpeggioNodes(
          fiddleKey, fiddleArpeggioType, fiddleTonality, fiddlePosition, fiddleStringFocus
        ),
        pairs: [],
      };
    } else if (isDoubleStops) {
      built = buildFiddleDoubleStopNodes(
        fiddleKey, formula, fiddleTonality, fiddleShape, fiddlePosition, fiddleStringFocus
      );
    } else {
      built = {
        nodes: buildFiddleNodes(
          fiddleKey, formula, fiddleTonality, fiddlePosition, fiddleStringFocus
        ),
        pairs: [],
      };
    }

    // Arpeggios ignore the filter: every note drawn is already a chord tone, so
    // 'Triads Only' would silently amputate the 7th of a Dom7. The controls hide
    // the row in this mode; this guard keeps a stale filter value from biting.
    let visible = built.nodes;
    if (!isArpeggios) {
      if (fiddleFilter === 'Roots Only')  visible = visible.filter(n => n.isRoot);
      if (fiddleFilter === 'Triads Only') visible = visible.filter(n => [0, 3, 4, 7].includes(n.interval));
    }

    // Drop any connector whose endpoints the filter removed — otherwise a bar
    // is left hanging off a note that is no longer drawn.
    let visiblePairs = built.pairs;
    if (visible !== built.nodes) {
      const shown = new Set(visible.map(n => `${n.string}-${n.semitone}`));
      visiblePairs = built.pairs.filter(p =>
        shown.has(`${p.sLow}-${p.nLow}`) && shown.has(`${p.sHigh}-${p.nHigh}`)
      );
    }

    return { notes: visible, pairs: visiblePairs };
  }, [isArpeggios, isDoubleStops, fiddleKey, formula, fiddleTonality, fiddleShape,
      fiddleArpeggioType, fiddlePosition, fiddleStringFocus, fiddleFilter]);

  // Only mark half steps between notes that are actually on screen — filtering
  // to Roots Only, for instance, leaves no adjacent pairs to mark.
  const halfSteps = useMemo(
    () => (showHalfSteps && !isDoubleStops ? findHalfStepPairs(notes) : []),
    [showHalfSteps, isDoubleStops, notes]
  );

  const positionFrame = FIDDLE_POSITIONS[fiddlePosition];

  const noteLabel = (node) => {
    if (!node.inPosition) return '';
    if (fiddleLabel === 'Note Names') return node.noteName;
    if (fiddleLabel === 'Intervals')  return node.degree ?? FIDDLE_DEGREE_LABELS[node.interval] ?? '';
    if (fiddleLabel === 'Fingering')  return node.finger === null ? '' : String(node.finger);
    return '';
  };

  const jointY    = round(noteY(TOTAL_SEMITONES_FIDDLE));
  const jointEdge = round(edgeAt(1));

  const mutedText = { fill: 'hsl(var(--ink-muted))' };
  const tapeFill  = 'hsl(var(--fiddle-tape))';

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        style={{ width: VIEW_W * k, maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
        className="select-none"
      >
        <defs>
          <linearGradient id={boardGradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0"    stopColor="hsl(var(--fiddle-board-hi))" />
            <stop offset="0.42" stopColor="hsl(var(--fiddle-board-mid))" />
            <stop offset="1"    stopColor="hsl(var(--fiddle-board-low))" />
          </linearGradient>
        </defs>

        {/* ── 1. Fingerboard ───────────────────────────────────────────── */}
        <polygon points={BOARD_POINTS} fill={`url(#${boardGradientId})`} />
        {/* The rim carries its own alpha and is set via style: SVG ignores a bare
            var() in a presentation attribute. */}
        <polygon
          points={BOARD_POINTS}
          fill="none" strokeWidth={px(1)}
          style={{ stroke: 'var(--fiddle-board-edge)' }}
        />

        {/* ── 2. Active position band ──────────────────────────────────── */}
        {positionFrame && (() => {
          const frac  = boardFraction(positionFrame.high);
          const edge  = round(edgeAt(frac));
          const bandY = round(NUT_Y + frac * BOARD_H);
          const points = [
            `${CX - EDGE_NUT},${NUT_Y}`,
            `${CX + EDGE_NUT},${NUT_Y}`,
            `${CX + edge},${bandY}`,
            `${CX - edge},${bandY}`,
          ].join(' ');
          const labelX = POSITION_LABEL_X;
          const labelY = round((NUT_Y + bandY) / 2);
          return (
            <g>
              <polygon points={points} fill={tapeFill} opacity={0.07} />
              <line
                x1={CX - edge} y1={bandY} x2={CX + edge} y2={bandY}
                stroke={tapeFill} strokeWidth={px(1)}
                strokeDasharray="3 4" opacity={0.45}
              />
              <text
                x={labelX} y={labelY}
                transform={`rotate(-90 ${labelX} ${labelY})`}
                textAnchor="middle"
                style={mutedText} fontSize={px(10)} fontWeight={800}
                letterSpacing="0.16em"
              >
                {`${fiddlePosition.toUpperCase()} POSITION`}
              </text>
            </g>
          );
        })()}

        {/* ── 3. Tape marks — fixed to the instrument, not to the key ──── */}
        {showTapes && FIDDLE_TAPE_SEMITONES.map((n, i) => {
          const frac = boardFraction(n);
          const edge = round(edgeAt(frac));
          const cy   = round(NUT_Y + frac * BOARD_H);
          return (
            <g key={`tape-${n}`}>
              <rect
                x={CX - edge} y={cy - 2.5} width={edge * 2} height={5}
                fill={tapeFill} opacity={0.16}
              />
              <line
                x1={CX + edge + 3} y1={cy} x2={CX + edge + 9} y2={cy}
                style={{ stroke: 'hsl(var(--ink-muted))' }} strokeWidth={px(1)} opacity={0.8}
              />
              <text
                x={CX + edge + 13} y={cy}
                dominantBaseline="central"
                style={mutedText} fontSize={px(10)} fontWeight={800}
              >
                {i + 1}
              </text>
            </g>
          );
        })}

        {/* ── 4. Neck joint — the octave, findable by feel ─────────────── */}
        <line
          x1={CX - jointEdge} y1={jointY} x2={CX + jointEdge} y2={jointY}
          stroke={tapeFill} strokeWidth={px(1.5)}
          strokeDasharray="5 4" opacity={0.4}
        />
        <text
          x={CX + jointEdge + 5} y={jointY}
          dominantBaseline="central"
          style={mutedText} fontSize={px(9)} fontWeight={800}
          letterSpacing="0.08em"
        >
          JOINT
        </text>

        {/* ── 5. Strings + names ───────────────────────────────────────── */}
        {FIDDLE_TUNING.map((_, s) => (
          <g key={`string-${s}`}>
            <line
              x1={round(stringX(s, 0))} y1={NUT_Y}
              x2={round(stringX(s, BOARD_BOTTOM_FRAC))} y2={BOARD_BOTTOM}
              style={{ stroke: 'hsl(var(--fiddle-string))' }}
              strokeWidth={STRING_WIDTH[s]}
              opacity={fiddleStringFocus[s] ? 1 : 0.2}
            />
            <text
              x={round(stringX(s, 0))} y={NAME_Y}
              textAnchor="middle" dominantBaseline="central"
              style={mutedText} fontSize={px(isDesktop ? 14 : 12)} fontWeight={700}
              opacity={fiddleStringFocus[s] ? 1 : 0.2}
            >
              {FIDDLE_STRING_NAMES[s]}
            </text>
          </g>
        ))}

        {/* ── 6. Nut ───────────────────────────────────────────────────── */}
        <rect
          x={CX - EDGE_NUT} y={NUT_Y - 2.5} width={EDGE_NUT * 2} height={5}
          style={{ fill: 'hsl(var(--fiddle-nut))' }}
        />

        {/* ── 7a. Half-step connectors — where two fingers touch ────────── */}
        {halfSteps.map(({ string, low, high }) => {
          const a = notePoint(string, low);
          const b = notePoint(string, high);
          return (
            <line
              key={`half-${string}-${low}`}
              x1={round(a.x)} y1={round(a.y)} x2={round(b.x)} y2={round(b.y)}
              stroke={tapeFill} strokeWidth={6}
              strokeLinecap="round" opacity={0.42}
            />
          );
        })}

        {/* ── 7b. Double-stop connectors ───────────────────────────────── */}
        {pairs.map(({ sLow, nLow, sHigh, nHigh }) => {
          const a = notePoint(sLow, nLow);
          const b = notePoint(sHigh, nHigh);
          return (
            <line
              key={`ds-${sLow}-${nLow}-${sHigh}-${nHigh}`}
              x1={round(a.x)} y1={round(a.y)} x2={round(b.x)} y2={round(b.y)}
              style={{ stroke: 'hsl(var(--sage))' }} strokeWidth={4}
              strokeLinecap="round" opacity={0.55}
            />
          );
        })}

        {/* ── 8. Notes — same dots as the guitar: roots solid terracotta with a
            white label; scale tones white with a sage ring and an ink label. Flat. */}
        {notes.map(node => {
          const { x, y } = notePoint(node.string, node.semitone);
          const cx  = round(x);
          const cy  = round(y);
          const lbl = noteLabel(node);
          const dim = !node.inPosition;
          return (
            <g
              key={`note-${node.string}-${node.semitone}`}
              className="animate-note-spring"
              style={{ animationDelay: `${node.semitone * 12}ms` }}
            >
              {/* Dimming lives on an inner group: the spring animation drives
                  opacity on the outer one, and sharing the property would make
                  out-of-position notes flash to full strength as they mount. */}
              <g opacity={dim ? 0.26 : 1}>
                <circle
                  cx={cx} cy={cy} r={NOTE_RADIUS - RING / 2}
                  strokeWidth={RING}
                  style={{
                    fill:   node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--note-scale-fill))',
                    stroke: node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--sage))',
                  }}
                />
                {lbl && (
                  <text
                    x={cx} y={cy}
                    textAnchor="middle" dominantBaseline="central"
                    fontSize={px(lbl.length > 2 ? NOTE_FONT_S : NOTE_FONT)} fontWeight={800}
                    className="pointer-events-none"
                    style={{ fill: node.isRoot ? 'hsl(var(--primary-foreground))' : 'hsl(var(--ink))' }}
                  >
                    {lbl}
                  </text>
                )}
              </g>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
